/**
 * ACTIVITY-CORE — sheet "Historial" del detalle de una entidad (orden,
 * producto…): historial local de esa entidad con "Deshacer este cambio" por
 * entrada (donde la política POS lo permite; el servidor rechaza el revert
 * sobre dinero cobrado). El Dialog base ya integra useBackDismiss.
 *
 * VERSION-HISTORY: pestaña "Versions" con la línea de versiones DERIVADA del
 * activity inmutable (v1..vN + sello temporal encadenado + quién/cuándo/qué),
 * sin tabla dedicada — ver packages/shared/src/activity/versions.ts.
 */
import { msg } from "@lingui/core/macro";
import { createSignal, For, Show } from "solid-js";
import { History } from "lucide-solid";
import {
  type ActivityRow,
  buildEntityVersions,
  type EntityVersion,
  getEntityActivityQuery,
  queryKeys,
} from "@comandero/shared";
import { createPowerSyncQuery } from "@comandero/shared/solid";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { useDrizzle } from "@/hooks/use-drizzle";
import { useSession } from "@/lib/auth";
import { useI18n } from "@/providers/i18n-provider";
import { Dialog } from "@/components/ui/dialog";
import { EmptyState } from "@/components/ui/empty-state";
import { formatRelativeTime } from "../lib/activity-text";
import { ActivityList } from "./activity-list";

const ENTITY_HISTORY_LIMIT = 100;

/** Clase del botón de pestaña Changes/Versions del header del diálogo. */
function tabClass(active: boolean): string {
  return `rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${
    active ? "bg-primary/10 text-primary" : "text-muted-foreground hover:text-foreground"
  }`;
}

interface EntityHistoryDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Tipo lógico de entidad (order, order_line, menu_product, payment…). */
  entityType: string;
  entityId: string;
  /** Oculta el botón de revert (p. ej. órdenes ya cobradas). */
  canRevert?: boolean;
}

/** Valor de un campo del patch, compacto y legible ("—" para null/ausente). */
function formatFieldValue(value: unknown): string {
  if (value === null || value === undefined) {
    return "—";
  }
  if (typeof value === "string") {
    return value;
  }
  return JSON.stringify(value);
}

function VersionEntryRow(props: { version: EntityVersion; currentUserId?: string }) {
  const { t, locale } = useI18n();

  const actorLabel = () => {
    if (props.version.actorUserId && props.version.actorUserId === props.currentUserId) {
      return t(msg`You`);
    }
    return props.version.actorName ?? t(msg`Someone`);
  };

  const serverTime = () =>
    props.version.serverTs ? new Date(props.version.serverTs).toLocaleString(locale()) : "";

  const changeEntries = () => Object.entries(props.version.changes);

  return (
    <div class="py-2.5" data-testid="version-entry">
      <div class="flex flex-wrap items-center gap-x-2 gap-y-1">
        <span
          class="inline-flex h-6 min-w-8 items-center justify-center rounded-full bg-primary/10 px-2 text-xs font-semibold text-primary"
          data-testid="version-badge"
        >
          v{props.version.version}
        </span>
        <span class="text-sm font-semibold" data-testid="version-actor">
          {actorLabel()}
        </span>
        <time
          class="text-xs text-muted-foreground"
          title={serverTime()}
          data-testid="version-server-ts"
        >
          {formatRelativeTime(props.version.serverTs, locale())}
        </time>
        <span
          class="ml-auto rounded bg-muted px-1.5 py-0.5 font-mono text-[10px] tracking-wide text-muted-foreground"
          title={t(msg`Seal`)}
          data-testid="version-seal"
        >
          {props.version.seal}
        </span>
      </div>
      <Show when={changeEntries().length > 0}>
        <ul class="mt-1.5 space-y-0.5 pl-1">
          <For each={changeEntries()}>
            {([field, change]) => (
              <li
                class="flex flex-wrap items-baseline gap-x-1.5 text-xs"
                data-testid="version-change"
              >
                <span class="font-mono text-muted-foreground">{field}</span>
                <span class="text-muted-foreground line-through decoration-destructive/50">
                  {formatFieldValue(change.old)}
                </span>
                <span aria-hidden="true" class="text-muted-foreground">
                  →
                </span>
                <span class="font-medium" data-testid="version-change-new">
                  {formatFieldValue(change.new)}
                </span>
              </li>
            )}
          </For>
        </ul>
      </Show>
    </div>
  );
}

export function EntityHistoryDialog(props: EntityHistoryDialogProps) {
  const { t } = useI18n();
  const { db } = usePowerSyncContext();
  const drizzle = useDrizzle();
  const session = useSession();
  const { userId } = session;
  const [view, setView] = createSignal<"changes" | "versions">("changes");

  const historyQuery = createPowerSyncQuery<ActivityRow>({
    queryKey: () => queryKeys.activity.entity(userId(), props.entityType, props.entityId),
    db,
    queryFn: () => {
      const dbInstance = drizzle();
      return dbInstance
        ? getEntityActivityQuery(dbInstance, props.entityType, props.entityId, {
            limit: ENTITY_HISTORY_LIMIT,
          })
        : null;
    },
    enabled: () => props.open && Boolean(drizzle()),
  });

  // Línea de versiones derivada (pura, local): mostrada DESC (la más nueva arriba).
  const versionsDesc = () => buildEntityVersions(historyQuery.data ?? []).toReversed();

  return (
    <Dialog open={props.open} onOpenChange={props.onOpenChange} contentClass="max-w-lg">
      <div class="flex max-h-[70vh] flex-col gap-4" data-testid="entity-history">
        <div class="flex items-center gap-3">
          <div class="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10 text-primary">
            <History class="h-4.5 w-4.5" />
          </div>
          <div class="min-w-0 flex-1">
            <h2 class="text-lg font-semibold">{t(msg`History`)}</h2>
            <p class="text-xs text-muted-foreground">
              {t(msg`Every change on this item, newest first.`)}
            </p>
          </div>
          <div class="flex shrink-0 gap-1 rounded-lg bg-muted/40 p-0.5" role="tablist">
            <button
              type="button"
              role="tab"
              aria-selected={view() === "changes"}
              class={tabClass(view() === "changes")}
              onClick={() => setView("changes")}
              data-testid="history-tab-changes"
            >
              {t(msg`Changes`)}
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={view() === "versions"}
              class={tabClass(view() === "versions")}
              onClick={() => setView("versions")}
              data-testid="history-tab-versions"
            >
              {t(msg`Versions`)}
            </button>
          </div>
        </div>
        <div class="min-h-0 overflow-y-auto pr-1">
          <Show
            when={view() === "versions"}
            fallback={
              <ActivityList
                rows={() => historyQuery.data ?? []}
                isLoading={() => historyQuery.isFetching && (historyQuery.data ?? []).length === 0}
                currentUserId={userId}
                canRevert={props.canRevert ?? true}
              />
            }
          >
            <Show
              when={versionsDesc().length > 0}
              fallback={
                <EmptyState
                  illustration="orders"
                  title={t(msg`No activity yet`)}
                  description={t(msg`Changes in your restaurant will show up here.`)}
                />
              }
            >
              <div class="divide-y divide-border/20" data-testid="entity-versions">
                <For each={versionsDesc()}>
                  {(version) => <VersionEntryRow version={version} currentUserId={userId()} />}
                </For>
              </div>
            </Show>
          </Show>
        </div>
      </div>
    </Dialog>
  );
}
